import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useProgramme, useMyApplications, APP_STATUS } from "@/hooks/useMember";
import ApplicationForm from "@/components/member/ApplicationForm";
import Reveal from "@/components/Reveal";
import NotFound from "@/pages/NotFound";

const statusLabel = { open: "Open for applications", waitlist: "Waitlist open", closed: "Closed", completed: "Completed" };

function Facts({ programme }) {
  const rows = [
    ["Product", programme.product?.name],
    ["Duration", programme.duration_weeks ? `${programme.duration_weeks} weeks` : "To be confirmed"],
    ["Cohort dates", programme.cohort_start ? `${programme.cohort_start} – ${programme.cohort_end}` : "To be confirmed"],
    ["Places", programme.capacity ? `${programme.capacity} (indicative)` : "To be confirmed"],
    ["Check-ins", (programme.checkin_days || []).map((d) => `Day ${d}`).join(" · ")],
  ];
  return (
    <dl className="divide-y divide-forest/10 border-y border-forest/10" data-testid="programme-facts">
      {rows.map(([k, v]) => (
        <div key={k} className="py-3 flex justify-between gap-6 text-sm"><dt className="text-charcoal/55">{k}</dt><dd className="text-right text-charcoal">{v}</dd></div>
      ))}
    </dl>
  );
}

export default function ProgrammeDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { data: p, isLoading, isError } = useProgramme(slug);
  const { data: apps = [] } = useMyApplications(!!user);
  const [applying, setApplying] = useState(false);

  if (isLoading) return <div className="py-32 text-center text-[11px] uppercase tracking-[0.24em] text-charcoal/50">Opening…</div>;
  if (isError || !p) return <NotFound />;

  const mine = apps.find((a) => a.programme_id === p.programme_id && !["withdrawn", "declined", "cancelled"].includes(a.status));
  const canApply = ["open", "waitlist"].includes(p.status);

  return (
    <div data-testid="programme-detail">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-12">
        <Link to="/programmes" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-charcoal/55 hover:text-forest transition-colors"><ArrowLeft size={14} /> Programmes</Link>
        <div className="mt-10 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8 animate-fade-up">
            <p className="eyebrow">{statusLabel[p.status] || p.status}</p>
            <h1 className="mt-6 font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-forest text-balance" data-testid="programme-title">{p.title}</h1>
          </div>
          <p className="lg:col-span-4 text-base sm:text-lg text-charcoal/65 font-light leading-relaxed animate-fade-up">{p.summary}</p>
        </div>
        <div className="hairline mt-12" />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-14">
          <Reveal>
            <p className="text-lg leading-relaxed text-charcoal/75 font-light">{p.description}</p>
            {p.sample && <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-charcoal/40" data-testid="programme-sample-note">Sample programme — details to be confirmed by ELA</p>}
          </Reveal>
          <Reveal>
            <h2 className="font-serif font-light text-3xl text-forest">Eligibility</h2>
            <ul className="mt-5 space-y-3">{p.eligibility.map((e, i) => <li key={i} className="flex gap-4 text-charcoal/75 font-light"><span className="numeral pt-1">{["I","II","III","IV","V","VI"][i]}</span>{e}</li>)}</ul>
          </Reveal>
          <Reveal>
            <h2 className="font-serif font-light text-3xl text-forest">Participation terms</h2>
            <ul className="mt-5 space-y-3">{p.terms.map((t, i) => <li key={i} className="flex gap-4 text-charcoal/75 font-light"><span className="w-6 shrink-0 h-px bg-champagne mt-3" />{t}</li>)}</ul>
            <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
              <div className="border border-forest/15 bg-cream p-5"><p className="text-[10px] uppercase tracking-[0.22em] text-champagne">Cost</p><p className="mt-2 text-charcoal/75 font-light">{p.cost_note}</p></div>
              <div className="border border-forest/15 bg-cream p-5"><p className="text-[10px] uppercase tracking-[0.22em] text-champagne">Shipping</p><p className="mt-2 text-charcoal/75 font-light">{p.shipping_note}</p></div>
            </div>
          </Reveal>

          {applying && user && !mine && (
            <Reveal><ApplicationForm programme={p} onDone={() => setApplying(false)} /></Reveal>
          )}
        </div>

        <aside className="lg:col-span-4 lg:col-start-9">
          <div className="lg:sticky lg:top-40 border border-forest/15 bg-cream p-7" data-testid="programme-apply-panel">
            {p.product?.image && (
              <div className="aspect-[4/5] bg-linen overflow-hidden mb-6"><img src={p.product.image} alt="" className="w-full h-full object-contain p-6 mix-blend-multiply" /></div>
            )}
            <Facts programme={p} />
            <div className="mt-6">
              {mine ? (
                <div data-testid="programme-my-status">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-champagne">Your application</p>
                  <p className="mt-2 font-serif text-2xl text-forest">{APP_STATUS[mine.status]?.label}</p>
                  <p className="mt-1 text-sm text-charcoal/65 font-light">{APP_STATUS[mine.status]?.text}</p>
                  <Link to="/my-rituals" className="btn btn-primary w-full mt-5">Track in My Rituals <ArrowRight size={14} /></Link>
                </div>
              ) : !canApply ? (
                <p className="text-sm text-charcoal/60 font-light">This programme is not accepting applications.</p>
              ) : !user ? (
                <>
                  <Link to={`/join?next=/programmes/${p.slug}`} className="btn btn-primary w-full" data-testid="programme-join-to-apply">Join to apply</Link>
                  <p className="mt-3 text-xs text-charcoal/50 font-light text-center">Free to join. Applying does not guarantee a product.</p>
                </>
              ) : (
                <>
                  <button onClick={() => { setApplying(true); setTimeout(() => document.querySelector("[data-testid='application-form']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150); }} className="btn btn-primary w-full" data-testid="programme-apply-button" disabled={applying}>
                    {applying ? "Application form below" : "Apply to this programme"}
                  </button>
                  <p className="mt-3 text-xs text-charcoal/50 font-light text-center">Selection depends on eligibility and availability.</p>
                </>
              )}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
